import type { Store } from '@reduxjs/toolkit'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import type { IState } from 'legacy/lut/store'
import { type ReactNode, useMemo } from 'react'
import { Provider, useStore } from 'react-redux'

import { usePartnerPlanUrl } from 'partner360/components/partnerNameWithLink'
import { NEW_VIEW_MODE_URL_PARAM_VALUE } from 'partner360/hooks/viewModeUrlParam'

import { type ICockpitPartnerScopeStatus, scopeCockpitStateToPartner } from '../../../lib'
import { useCockpitPartnerLinkParams } from '../cockpitHierarchyTable/useCockpitPartnerLinkParams'
import { CockpitRemotePartnerProvider } from '../cockpitSectionNavigation'

type TPartner360State = IState['partner360']

/**
 * The store the sections read, reduced to one partner. Reads are cached per slice, so a dispatch
 * elsewhere in the app hands the selectors the same slices back and nothing recomputes.
 *
 * Besides the cockpit slice, the partner page's configuration is told whether the partner is a
 * distributor: the shared dialogs name the partner's roles by it. Nothing else of the page's own
 * partner is taken over — its access rights stay the reader's.
 */
const createPartnerScopedStore = (
  store: Store<IState>,
  partnerId: string,
  status: ICockpitPartnerScopeStatus | null
): Store<IState> => {
  let lastRoot: IState | null = null
  let lastRootPartner360: TPartner360State | null = null
  let lastCockpit: TPartner360State['financialCockpit'] | null = null
  let lastConfiguration: TPartner360State['configuration'] | null = null
  let lastPartner360: TPartner360State | null = null
  let lastScoped: IState | null = null

  const scopePartner360 = (partner360: TPartner360State): TPartner360State => {
    const financialCockpit = scopeCockpitStateToPartner(
      partner360.financialCockpit,
      partnerId,
      status
    )
    const isDistributor = financialCockpit.partnerData[0]?.IsDistributor === 1
    const { configuration } = partner360

    return {
      ...partner360,
      financialCockpit,
      configuration: {
        ...configuration,
        data: {
          ...configuration.data,
          partnerAccess: { ...configuration.data.partnerAccess, isDistributor },
        },
      },
    }
  }

  const getState = () => {
    const root = store.getState()

    if (root === lastRoot && lastScoped) {
      return lastScoped
    }

    const { financialCockpit, configuration } = root.partner360

    if (
      !lastPartner360 ||
      financialCockpit !== lastCockpit ||
      configuration !== lastConfiguration
    ) {
      lastCockpit = financialCockpit
      lastConfiguration = configuration
      lastPartner360 = scopePartner360(root.partner360)
    } else if (root.partner360 !== lastRootPartner360) {
      lastPartner360 = {
        ...root.partner360,
        financialCockpit: lastPartner360.financialCockpit,
        configuration: lastPartner360.configuration,
      }
    }

    lastRoot = root
    lastRootPartner360 = root.partner360
    lastScoped = { ...root, partner360: lastPartner360 }

    return lastScoped
  }

  return { ...store, getState }
}

interface ICockpitPartnerScopeProps {
  partnerId: string
  partnerName: string
  status: ICockpitPartnerScopeStatus | null
  children: ReactNode
}

/**
 * Renders parts written for the single-partner cockpit — the attention status sections — for one
 * partner of the manager cockpit.
 *
 * Those parts are written for a payload of one partner and read it through the cockpit's own
 * selectors. The manager payload holds every partner's rows already, so the sections get the same
 * store reduced to this partner; what they dispatch still reaches the real one.
 */
export const CockpitPartnerScope = ({
  partnerId,
  partnerName,
  status,
  children,
}: ICockpitPartnerScopeProps) => {
  const store = useStore<IState>()
  const bitValue = status?.bitValue ?? null
  const offering = status?.offering ?? null

  const scopedStore = useMemo(
    () =>
      createPartnerScopedStore(store, partnerId, bitValue === null ? null : { bitValue, offering }),
    [store, partnerId, bitValue, offering]
  )

  const { fYear, offering: cockpitOffering } = useCockpitPartnerLinkParams()
  const partnerUrl = usePartnerPlanUrl({
    partnerId,
    fYear,
    programType: offering && offering !== ProgramTypes.Overall ? offering : cockpitOffering,
    viewMode: NEW_VIEW_MODE_URL_PARAM_VALUE,
  })

  const remote = useMemo(
    () => ({ partnerId, partnerName, partnerUrl }),
    [partnerId, partnerName, partnerUrl]
  )

  return (
    <Provider store={scopedStore}>
      <CockpitRemotePartnerProvider value={remote}>{children}</CockpitRemotePartnerProvider>
    </Provider>
  )
}

CockpitPartnerScope.displayName = 'CockpitPartnerScope'
